/*
  Quas analytics: behaviour events for GA4.

  The theme publishes named custom events through Shopify's customer events
  API (Shopify.analytics.publish). The Quas custom pixel (Dev/Shopify/analytics/
  custom-pixel.js, installed in Settings > Customer events) subscribes to them
  and forwards them to GA4, so collection follows the store's consent settings
  and no tag runs directly in the page.

  Standard shop events (page_view, view_item, add_to_cart, checkout, purchase)
  already come from the Google & YouTube app pixel; this file only adds what
  that pixel cannot see: how people use the picker, the page sections and the
  site's own controls. Sign-ups, enquiries and stockist searches also go to
  Meta as standard events through the Facebook & Instagram app's pixel. Event names and parameters are documented in
  Dev/Shopify/docs/analytics-tracking-plan.md. Turn on console logging with
  localStorage.setItem('quas_analytics_debug', '1').
*/
(() => {
  if (window.QuasAnalytics) return;

  const context = window.QuasAnalyticsContext || {};
  const queue = [];
  const debug = (() => {
    try {
      return window.localStorage.getItem('quas_analytics_debug') === '1';
    } catch (error) {
      return false;
    }
  })();

  const clean = (value, max = 100) =>
    String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max);

  function publish(name, params) {
    const api = window.Shopify && window.Shopify.analytics;
    if (!api || typeof api.publish !== 'function') {
      queue.push([name, params]);
      return;
    }
    api.publish(name, params);
  }

  function flush() {
    const api = window.Shopify && window.Shopify.analytics;
    if (!api || typeof api.publish !== 'function') return false;
    while (queue.length) api.publish(...queue.shift());
    return true;
  }

  function track(name, params = {}) {
    const payload = {
      page_template: clean(context.template, 40),
      product_handle: clean(context.productHandle, 80),
      ...params
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === '' || payload[key] == null) delete payload[key];
    });
    if (debug) console.log('[quas-analytics]', name, payload);
    publish(name, payload);
    // Clarity: the event name makes recordings filterable (for example every
    // session where the fridge tick blocked Add to cart).
    if (typeof window.clarity === 'function') window.clarity('event', name);
  }

  if (typeof window.clarity === 'function' && context.template) window.clarity('set', 'page_template', String(context.template));

  // Meta: the Facebook & Instagram app loads Meta's pixel in the page when
  // the visitor's consent allows it, and its own events cover the shop
  // journey (views, carts, checkout, purchase, search). These standard events
  // add what it cannot see. They wait up to ten seconds for the app's pixel
  // and are dropped without it, so consent stays with the app.
  function metaTrack(event, params = {}) {
    const send = () => {
      if (typeof window.fbq !== 'function') return false;
      window.fbq('track', event, params);
      if (debug) console.log('[quas-analytics] Meta', event, params);
      return true;
    };
    if (send()) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (send() || tries > 40) window.clearInterval(timer);
    }, 250);
  }

  window.QuasAnalytics = { track, metaTrack };

  // The pixel manager loads after the theme scripts on some pages.
  if (!flush()) {
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (flush() || tries > 40) window.clearInterval(timer);
    }, 250);
  }

  const sectionName = (element) => {
    const section = element && element.closest('.shopify-section');
    if (!section || !section.id) return '';
    return clean(section.id.split('__').pop(), 40);
  };

  /* ------------------------------------------------------------ picker */

  const picker = () => document.querySelector('.quas-picker-container');

  function pickerState() {
    const root = picker();
    if (!root) return {};
    const flavours = (root.dataset.stageFlavours || '').trim().split(/\s+/).filter(Boolean);
    const size = root.querySelector('.quas-picker-size-card.selected');
    const subscribing = root.querySelector('.js-purchase-subscribe');
    const plan = root.querySelector('.quas-plan-chip[aria-pressed="true"]');
    return {
      flavours: flavours.join('+'),
      pack_size: size ? Number(size.dataset.size) || clean(size.dataset.size, 4) : undefined,
      purchase_type: subscribing && subscribing.checked ? 'subscription' : 'one_off',
      frequency: subscribing && subscribing.checked && plan ? clean(plan.firstChild && plan.firstChild.textContent, 20) : undefined
    };
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const flavourCard = target.closest('.quas-picker-flavour-card');
      if (flavourCard) {
        const wasSelected = flavourCard.classList.contains('selected');
        track('quas_flavour_toggle', {
          flavour: clean(flavourCard.dataset.flavour, 4),
          action: wasSelected ? 'remove' : 'add'
        });
        return;
      }

      const sizeCard = target.closest('.quas-picker-size-card');
      if (sizeCard && !sizeCard.classList.contains('selected')) {
        track('quas_pack_size_select', { pack_size: Number(sizeCard.dataset.size) || clean(sizeCard.dataset.size, 4) });
        return;
      }

      const chip = target.closest('.quas-plan-chip');
      if (chip && chip.getAttribute('aria-pressed') !== 'true') {
        track('quas_subscription_frequency_select', { frequency: clean(chip.firstChild && chip.firstChild.textContent, 20), ...pickerState() });
        return;
      }

      if (target.closest('.js-size-note-action')) {
        track('quas_size_note_action', pickerState());
        return;
      }

      if (target.closest('.js-express-lock')) {
        track('quas_fridge_ack_blocked', { trigger: 'express', ...pickerState() });
        return;
      }

      // Wallet buttons render in a shadow root; the click retargets to the host.
      if (target.closest('.js-express')) {
        track('quas_express_checkout_click', pickerState());
        return;
      }

      if (target.closest('.js-subscription-info')) {
        track('quas_subscription_info_open', pickerState());
        return;
      }

      const promise = target.closest('.js-promise-trigger');
      if (promise) {
        const item = promise.closest('.js-promise');
        const key = item ? (item.className.match(/quas-picker-promise--([\w-]+)/) || [])[1] : '';
        if (promise.getAttribute('aria-expanded') !== 'true') track('quas_promise_open', { promise: clean(key, 20) });
        return;
      }

      const navLink = target.closest('.js-section-nav-link');
      if (navLink) {
        track('quas_section_nav_click', { anchor: clean((navLink.getAttribute('href') || '').replace('#', ''), 40), label: clean(navLink.textContent, 40) });
        return;
      }

      const menuLink = target.closest('.header-section .menu-list__link, .header-section .mega-menu__link, .header-section .quas-menu-all');
      if (menuLink) {
        track('quas_menu_click', { label: clean(menuLink.textContent, 60), destination: clean(menuLink.getAttribute('href'), 120) });
        return;
      }

      const cta = target.closest('a.quas-front-cta, a.quas-home-button, a.quas-section__link, .quas-page-hero a, .quas-flavour-card');
      if (cta) {
        track('quas_cta_click', {
          label: clean(cta.querySelector('h3, .quas-flavour-card__name') ? cta.querySelector('h3, .quas-flavour-card__name').textContent : cta.textContent, 60),
          section: sectionName(cta),
          destination: clean(cta.getAttribute('href'), 120)
        });
        return;
      }

      const link = target.closest('a[href]');
      if (link && link.hostname && link.hostname !== window.location.hostname && /^https?:/.test(link.protocol)) {
        track('quas_outbound_click', { destination_host: clean(link.hostname, 80), section: sectionName(link), label: clean(link.textContent, 60) });
      }
    },
    true
  );

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('.js-purchase-once, .js-purchase-subscribe') && target.checked) {
      track('quas_purchase_type_select', { ...pickerState(), purchase_type: target.matches('.js-purchase-subscribe') ? 'subscription' : 'one_off' });
    } else if (target.matches('.js-live-ack-checkbox') && target.checked) {
      track('quas_fridge_ack_checked', pickerState());
    }
  });

  // Submit runs before the picker's own guard decides, so the state read here
  // tells us whether the tick was missing.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      // The picker form has a field named "id", which shadows form.id.
      if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== 'quas-product-picker-form') return;
      const ack = form.querySelector('.js-live-ack-checkbox');
      const submitter = event.submitter;
      // Not "source": GA4 reads an event parameter of that name as the
      // session's traffic source.
      const trigger = submitter && submitter.closest('.quas-sticky-atc') ? 'sticky_bar' : submitter && submitter.closest('.quas-section-nav') ? 'section_menu' : 'main_button';
      if (ack && !ack.checked) {
        track('quas_fridge_ack_blocked', { trigger, ...pickerState() });
        return;
      }
      const price = form.closest('.quas-picker-container') && form.closest('.quas-picker-container').querySelector('.js-atc-price');
      track('quas_add_to_cart_attempt', { trigger, price: clean(price && price.textContent, 20), ...pickerState() });
    },
    true
  );

  /* ------------------------------------------------------------ stockists and trade */

  // Search terms are not sent, only their length and the visible result
  // count, so no personal data (for example a home postcode) reaches GA4.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.matches('[data-stockist-search]')) {
        const input = form.querySelector('input[type="search"]');
        window.setTimeout(() => {
          const status = document.querySelector('[data-stockist-status]');
          const count = status ? parseInt((status.textContent.match(/\d+/) || ['0'])[0], 10) : undefined;
          track('quas_stockist_search', { query_length: input ? input.value.trim().length : 0, results: count });
          metaTrack('FindLocation', { content_name: 'Stockist search', content_category: 'stockists' });
        }, 400);
      } else if (form.matches('.quas-trade__form')) {
        track('quas_stockist_enquiry_submit', { section: sectionName(form) });
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const filter = target.closest('[data-stockist-filter]');
      if (filter && filter.getAttribute('aria-pressed') !== 'true') track('quas_stockist_filter', { filter: clean(filter.dataset.stockistFilter, 40) });
    },
    true
  );

  /* ------------------------------------------------------------ sign-ups and enquiries */

  // Shopify reloads the page after a form posts, so a sign-up or enquiry
  // counts only when its success message is on the page, never on the
  // submit click. A reload of the same success page is not counted twice.
  // No email address or message text is sent anywhere.
  const POSTED_FORMS = [
    {
      selector: '[id^="QuasFooterNewsletterSuccess-"], .quas-welcome__form-message.is-success, [id^="Email-signup__message-success-"]',
      ga4: 'quas_newsletter_signup',
      meta: ['Lead', { content_name: 'Newsletter', content_category: 'newsletter' }]
    },
    {
      selector: '.quas-trade__status--success',
      ga4: 'quas_stockist_enquiry_sent',
      meta: ['SubmitApplication', { content_name: 'Become a stockist', content_category: 'wholesale' }]
    },
    {
      selector: '.contact-form__success',
      ga4: 'quas_contact_form_sent',
      meta: ['Contact', { content_name: 'Contact form', content_category: 'contact' }]
    }
  ];

  POSTED_FORMS.forEach(({ selector, ga4, meta }) => {
    const message = document.querySelector(selector);
    if (!message) return;
    const key = `quas_posted:${ga4}:${window.location.pathname}${window.location.search}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, '1');
    } catch (error) {
      // Without storage a reload may count twice; the event still matters more.
    }
    track(ga4, { section: sectionName(message) });
    metaTrack(...meta);
  });

  /* ------------------------------------------------------------ FAQs and disclosures */

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      const summary = details.querySelector('summary');
      track('quas_faq_open', { question: clean(summary && summary.textContent, 100), section: sectionName(details) });
    },
    true
  );

  /* ------------------------------------------------------------ journey through the page */

  // One event per section per page view, when the section crosses the middle
  // of the screen, however tall it is. Together they show where people stop
  // reading.
  if ('IntersectionObserver' in window) {
    const seen = new Set();
    const sections = Array.from(document.querySelectorAll('main .shopify-section'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const name = sectionName(entry.target.firstElementChild || entry.target);
          if (!name || seen.has(name)) return;
          seen.add(name);
          observer.unobserve(entry.target);
          track('quas_section_view', { section: name, section_index: sections.indexOf(entry.target) + 1 });
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    sections.forEach((section) => {
      if (section.offsetHeight > 0) observer.observe(section);
    });
  }

  const depths = [25, 50, 75, 90];
  const reached = new Set();
  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        const percent = Math.round((window.scrollY / max) * 100);
        depths.forEach((depth) => {
          if (percent >= depth && !reached.has(depth)) {
            reached.add(depth);
            track('quas_scroll_depth', { percent_scrolled: depth });
          }
        });
      });
    },
    { passive: true }
  );
})();
